import { PlayerPackDrawer, PlayerPacksRow } from '@bo-components/playerPacks';
import {
  Table,
  TableBodyInfiniteScroll,
  TableHeader
} from '@library/components/table';
import { useCallback, useMemo, useState } from 'react';
import { NoResults } from '@library/components/NoResults';
import { PLAYER_PACKS_TABLE_HEADER } from '@bo-constants/playerPacks';
import type { PlayerPack } from '@library/types/playerPacks/playerPacks';
import { usePlayerPacksTable } from '@bo-hooks/playerPacks/usePlayerPacksTable';

export interface PlayerPacksTableProps {
  onPageChange: () => void;
}

export const PlayerPacksTable = () => {
  const { currentPage, playerPacks, onPageChange, totalPages, leaguesList } =
    usePlayerPacksTable();

  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [currentPlayerPack, setCurrentPlayerPack] = useState<PlayerPack>(
    {} as PlayerPack
  );

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false);
  }, [setIsDrawerOpen]);

  const handleClickPlayerPack = useCallback(
    (playerPackId: string) => {
      setCurrentPlayerPack(
        () =>
          playerPacks.find((pack) => pack.id === playerPackId) ||
          ({} as PlayerPack)
      );
      setIsDrawerOpen(true);
    },
    [playerPacks]
  );

  const infiniteScrollProps = useMemo(
    () => ({
      dataLength: Object.values(playerPacks).length,
      hasMore: totalPages > currentPage,
      loader: <p>Loading...</p>,
      next: onPageChange
    }),
    [playerPacks, currentPage, onPageChange, totalPages]
  );

  if (!playerPacks) {
    return (
      <NoResults
        className="m-0"
        dataTestId="no-player-packs"
        label="PlayerPacks not found"
      />
    );
  }

  return (
    <>
      <Table
        className="border-soFresh-140 overflow-x-auto rounded-lg border p-0"
        dataTestId="player-packs-list"
      >
        <TableHeader className="pl-4" headers={PLAYER_PACKS_TABLE_HEADER} />
        <TableBodyInfiniteScroll
          className="overflow-x-auto"
          bodyHeight="[calc(100vh-22rem)]"
          bodyMaxHeight="max-h-[calc(100vh-22rem)]"
          infiniteScrollProps={infiniteScrollProps}
          scrollId="PlayerPacksListTableBody"
        >
          {playerPacks.map((playerPack) => (
            <PlayerPacksRow
              key={playerPack.id}
              playerPack={playerPack}
              onClickPlayerPack={handleClickPlayerPack}
            />
          ))}
        </TableBodyInfiniteScroll>
      </Table>
      <PlayerPackDrawer
        isOpen={isDrawerOpen}
        onCloseDrawer={handleCloseDrawer}
        playerPack={currentPlayerPack}
        leaguesList={leaguesList}
      />
    </>
  );
};
